// FILE: frontend/src/components/kyb/sections/SignalsDisplay.tsx
// FIXED: Full-width layout when only positive OR negative signals exist
// Side-by-side layout only when BOTH exist

import React from 'react';
import { CheckCircle, AlertTriangle, ExternalLink } from 'lucide-react';
import { GlassCard } from '../../ui/GlassCard';  


interface Signal {
  type?: string;
  description: string;
  sources?: string[];
  strength?: string;
}

interface SignalsDisplayProps {
  positiveSignals: Signal[];
  negativeSignals: Signal[];
}

export const SignalsDisplay: React.FC<SignalsDisplayProps> = ({ 
  positiveSignals = [], 
  negativeSignals = [] 
}) => {
  const hasPositive = positiveSignals && positiveSignals.length > 0;
  const hasNegative = negativeSignals && negativeSignals.length > 0;

  // If no signals at all, don't render
  if (!hasPositive && !hasNegative) {
    return null;
  }

  // LOGIC: Use full width if only one type exists, side-by-side if both exist
  const useSideBySide = hasPositive && hasNegative;

  return (
    <div className={useSideBySide ? "grid md:grid-cols-2 gap-6" : "w-full"}>
      {/* Positive Signals */}
      {hasPositive && (
        <GlassCard className={`p-6 ${!useSideBySide ? 'w-full' : ''}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-900">Positive Signals</h3>
              <p className="text-sm text-slate-600">
                {positiveSignals.length} indicator{positiveSignals.length !== 1 ? 's' : ''} found
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {positiveSignals.map((signal, idx) => (
              <div 
                key={idx} 
                className="p-4 bg-green-50 border border-green-200 rounded-lg hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    {signal.type && (
                      <span className="inline-block px-2 py-1 bg-green-200 text-green-800 text-xs font-semibold rounded mb-2 uppercase">
                        {signal.type}
                      </span>
                    )}
                    <p className="text-sm text-slate-800 font-medium">
                      {signal.description}
                    </p>
                    
                    {signal.sources && signal.sources.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {signal.sources.map((source, sourceIdx) => (
                          <a
                            key={sourceIdx}
                            href={source}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-green-700 hover:text-green-900 hover:underline"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Source {sourceIdx + 1}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      {/* Negative Signals */}
      {hasNegative && (
        <GlassCard className={`p-6 ${!useSideBySide ? 'w-full' : ''}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-orange-100 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-900">Risk Signals</h3>
              <p className="text-sm text-slate-600">
                {negativeSignals.length} concern{negativeSignals.length !== 1 ? 's' : ''} found
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {negativeSignals.map((signal, idx) => (
              <div 
                key={idx} 
                className="p-4 bg-orange-50 border border-orange-200 rounded-lg hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    {signal.type && (
                      <span className="inline-block px-2 py-1 bg-orange-200 text-orange-800 text-xs font-semibold rounded mb-2 uppercase">
                        {signal.type}
                      </span>
                    )}
                    <p className="text-sm text-slate-800 font-medium">
                      {signal.description}
                    </p>
                    
                    {signal.sources && signal.sources.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {signal.sources.map((source, sourceIdx) => (
                          <a
                            key={sourceIdx}
                            href={source}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-orange-700 hover:text-orange-900 hover:underline"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Source {sourceIdx + 1}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      )}
    </div>
  );
};